// ═══════════════════════════════════════════════════════════════════
//  GRID COLUMNS
//
//  Who each column of the calendar belongs to: the team's actual roster,
//  from GET /api/ams/teams/{id}/members, named by joining to the Cal
//  organization's users (GET /api/ams/cal-users) — membership carries a
//  user id and nothing else.
//
//  Deliberately the roster and not the bookings' hosts: a member with a
//  free day is exactly who an admin is looking for, and deriving columns
//  from bookings would make them vanish.
// ═══════════════════════════════════════════════════════════════════

import type { CalOrganizationUser, CalTeamMember } from "../bookingCalendarApi"
import type { GridColumn } from "./dayGrid"

export interface StaffColumn extends GridColumn {
  name: string
  initials: string
  color: string
  light: string
  /** False for someone invited to the team who has not accepted — they take no bookings yet. */
  accepted: boolean
  role?: string
}

// Matches the --staff-N pairs in bookingCalendar.css. Assigned by position so a given roster always
// colours the same way within a view.
const PALETTE = [
  { color: "#7c3aed", light: "#ede9fe" },
  { color: "#0369a1", light: "#e0f2fe" },
  { color: "#b45309", light: "#fef3c7" },
  { color: "#0f766e", light: "#ccfbf1" },
  { color: "#be185d", light: "#fce7f3" },
] as const

function paletteAt(index: number): { color: string; light: string } {
  return PALETTE[index % PALETTE.length]!
}

/** "Ada Lovelace" → "AL". Empty when there is no name to take them from. */
export function initialsOf(name: string | null | undefined): string {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? []
  return parts.map(part => part[0]!).join("").toUpperCase().slice(0, 2)
}

/**
 * A column per team member, in name order.
 *
 * `users` is the whole organization; only the members are drawn. A member the organization listing
 * does not name still gets a column identified by their id — an unnamed column is a roster gap to
 * notice, not a person to drop from the day.
 */
export function memberColumns(
  members: CalTeamMember[],
  users: CalOrganizationUser[],
): StaffColumn[] {
  const byId = new Map(users.map(user => [user.id, user]))

  return members
    .map(member => {
      const user = byId.get(member.userId)
      const name = user?.name?.trim() || user?.username?.trim() || user?.email?.trim() || ""
      return { member, name }
    })
    .sort((a, b) =>
      (a.name || `~${a.member.userId}`).localeCompare(b.name || `~${b.member.userId}`)
      || a.member.userId - b.member.userId)
    .map(({ member, name }, index) => ({
      id: `cal-${member.userId}`,
      calUserId: member.userId,
      name: name || `Staff #${member.userId}`,
      initials: name ? initialsOf(name) : `#${member.userId}`,
      accepted: member.accepted,
      role: member.role,
      ...paletteAt(index),
    }))
}
